import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import rough from "roughjs/bin/rough";

import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { renderStaticScene } from "@excalidraw/excalidraw/renderer/staticScene";
import { THEME, arrayToMap, toBrandedType } from "@excalidraw/common";
import {
  clearAnimationOverrides,
  getElementsOverlappingFrame,
  getInitializedImageElements,
  getTextureFileId,
  setAnimationOverrides,
  updateImageCache,
} from "@excalidraw/element";

import type { AnimatedValues } from "@excalidraw/element";
import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
  FileId,
  NonDeletedSceneElementsMap,
} from "@excalidraw/element/types";
import type { RenderableElementsMap } from "@excalidraw/excalidraw/scene/types";
import type {
  AppClassProperties,
  AppState,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";

import { getScreenDurationMs } from "../animation/types";
import {
  clearPageIds,
  deletePageIds,
  getFlipbookBooks,
  getFlipbookPages,
} from "../flipbook/ops";
import { useFlipbook } from "../flipbook/FlipbookContext";
import {
  resolveBookId,
  useFlipbookActions,
} from "../flipbook/useFlipbookActions";

import { observeVisibility } from "./visibility";

import "./FlipbookBar.scss";

import type { PlayMode } from "../flipbook/FlipbookContext";

const THUMB_HEIGHT = 52;
/** Pages visible in the filmstrip before it scrolls horizontally. */
const VISIBLE_THUMBS = 8;
/** Gap + padding of `.flipbook-bar__strip`, px — kept in sync with the SCSS
 * so the strip can be capped at exactly VISIBLE_THUMBS pages. */
const STRIP_GAP = 5;
const STRIP_PADDING = 2;

/** Thumbnail width for a page, from its aspect ratio (height is fixed). */
const thumbWidth = (frame: { width: number; height: number }) =>
  Math.max(
    28,
    Math.min(104, Math.round((frame.width / frame.height) * THUMB_HEIGHT)),
  );

/**
 * A `.flipbook-menu` popover placed at viewport coordinates — the right-click
 * page menu (both surfaces) and the panel's "copy to screen" screen picker.
 *
 * It portals to <body> because `position: fixed` is NOT viewport-relative
 * inside every ancestor: a transform, filter or backdrop-filter on one makes
 * that element the containing block. Both surfaces hit this — the bottom
 * dock has `translateX(-50%)`, and the sidebar the panel lives in has
 * `backdrop-filter`, which put the panel's menus ~1200px off the right edge.
 * <body> has no such ancestor, so left/top mean what they say again.
 *
 * It also nudges itself back inside the viewport (the menu opens upward via
 * a CSS transform, so this measures the real box rather than assuming) and
 * owns the two dismissals every popover needs: pointerdown outside, Escape.
 */
export const FloatingMenu = ({
  x,
  y,
  placement = "above",
  minWidth,
  onClose,
  children,
}: {
  x: number;
  y: number;
  /** "above" (default) is the context-menu behaviour — the menu grows up from
   * the cursor. "below" anchors a dropdown under its button. */
  placement?: "above" | "below";
  minWidth?: number;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    setPos({ left: x, top: y });
    const el = ref.current;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    const dx =
      Math.min(0, window.innerWidth - 8 - r.right) + Math.max(0, 8 - r.left);
    const dy =
      Math.min(0, window.innerHeight - 8 - r.bottom) + Math.max(0, 8 - r.top);
    if (dx || dy) {
      setPos({ left: x + dx, top: y + dy });
    }
  }, [x, y]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest(".flipbook-menu")) {
        onClose();
      }
    };
    // capture, and swallowed: Escape reaches Excalidraw's own handler
    // otherwise, which closes the whole sidebar out from under the menu
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className={`flipbook-menu${
        placement === "below" ? " flipbook-menu--below" : ""
      }`}
      style={{ ...pos, minWidth }}
      onClick={onClose}
    >
      {children}
    </div>,
    document.body,
  );
};

/** Fired on every flipbook page swap (detail: { stageId }) and once on stop
 * (no detail) so passive canvases like the mini preview can repaint. */
export const FLIPBOOK_TICK_EVENT = "chartoon:flipbook-tick";

/** Fired by other UI (e.g. the animation bar's flipbook button) to open the
 * flipbook strip. */
export const FLIPBOOK_OPEN_EVENT = "chartoon:flipbook-open";

/** Shared image/texture cache for the app's own static renders (flipbook
 * thumbnails, mini preview). renderStaticScene resolves images synchronously
 * from its cache — an empty per-render Map left image elements blank. */
export const sharedImageCache: AppClassProperties["imageCache"] = new Map();

/** Loads any image/texture files the given elements need that aren't cached
 * yet, then calls `onLoaded` so the caller can repaint with them.
 * ponytail: cache is append-only — a replaced file keeps its old bitmap until
 * reload; add invalidation if in-place file edits ever become a thing. */
export const ensureImageCache = (
  elements: readonly ExcalidrawElement[],
  files: BinaryFiles,
  onLoaded: () => void,
) => {
  const missing = [
    ...getInitializedImageElements(elements).map((el) => el.fileId),
    ...elements
      .map((el) => getTextureFileId(el))
      .filter((id): id is FileId => id !== null),
  ].filter((id) => !sharedImageCache.has(id));
  if (missing.length > 0) {
    updateImageCache({
      imageCache: sharedImageCache,
      fileIds: missing,
      files,
    }).then(onLoaded);
  }
};

/** Filmstrip glyph shared by the trigger pill and the animation-bar button. */
export const filmstripIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <rect x="2" y="4" width="16" height="12" rx="2" />
    <path d="M6 4v12M14 4v12M2 8h4M2 12h4M14 8h4M14 12h4" />
  </svg>
);

// onion-skin glyph: two offset layers
const onionIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="11" height="11" rx="2" opacity="0.45" />
    <rect x="6" y="6" width="11" height="11" rx="2" />
  </svg>
);

/** Shared wrapper for the panel's line icons — one stroke weight throughout
 * keeps the header reading as a single set rather than mixed glyphs. */
const lineIcon = (path: React.ReactNode, size = 15) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {path}
  </svg>
);

const playIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    width="14"
    height="14"
    fill="currentColor"
  >
    <path d="M6.2 3.6a.9.9 0 0 1 1.36-.78l8.1 5.4a.9.9 0 0 1 0 1.56l-8.1 5.4A.9.9 0 0 1 6.2 14.4z" />
  </svg>
);

const pauseIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    width="14"
    height="14"
    fill="currentColor"
  >
    <rect x="4.6" y="3.4" width="3.6" height="13.2" rx="1.4" />
    <rect x="11.8" y="3.4" width="3.6" height="13.2" rx="1.4" />
  </svg>
);

const addIcon = lineIcon(<path d="M10 4.5v11M4.5 10h11" />, 16);
const closeIcon = lineIcon(<path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />);

const PLAY_MODES: { id: PlayMode; icon: React.ReactNode; label: string }[] = [
  {
    id: "loop",
    label: "Loop",
    icon: lineIcon(
      <>
        <path d="M16.4 10a6.4 6.4 0 1 1-1.9-4.5" />
        <path d="M16.4 3.4v3.2h-3.2" />
      </>,
    ),
  },
  {
    id: "pingpong",
    label: "Ping-pong",
    icon: lineIcon(
      <>
        <path d="M4 7.5h12M6.2 5.3 4 7.5l2.2 2.2" />
        <path d="M16 12.5H4M13.8 10.3l2.2 2.2-2.2 2.2" />
      </>,
    ),
  },
  {
    id: "once",
    label: "Play once",
    icon: lineIcon(
      <>
        <path d="M3.5 10h10M11.3 7.2l2.8 2.8-2.8 2.8" />
        <path d="M16.5 5.5v9" />
      </>,
    ),
  },
];

// Procreate-style onion tints: past frames red, future frames green
const TINT_PREV = "#e03131";
const TINT_NEXT = "#2f9e44";

/** Procreate-Dreams-style flipbook timeline: a bottom filmstrip of frame
 * ("page") thumbnails with play, add, onion skin, FPS, and a per-frame
 * context menu (duplicate / clear / delete). Pages are ordinary screens
 * (frame elements), so the Settings tab's sequence export works unchanged. */
export const FlipbookBar = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const {
    open,
    setOpen,
    activeId,
    setActiveId,
    playing,
    setPlaying,
    playMode,
    setPlayMode,
    onion,
    setOnion,
    fps,
    setFps,
    flipbookActive,
    activeBookId,
    setActiveBookId,
  } = useFlipbook();
  const [onionMenuOpen, setOnionMenuOpen] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [menu, setMenu] = useState<{
    frameId: string;
    x: number;
    y: number;
  } | null>(null);
  const onionOverlayRef = useRef<HTMLCanvasElement>(null);
  const playTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);

  // the dock is `position: fixed` but still a DOM descendant of Excalidraw's
  // container, which binds its own native (non-React) wheel listener there
  // for canvas pan/zoom — a thumbnail's <canvas> matches that listener's
  // target check, so scrolling the strip panned the main canvas instead.
  // React's onWheel prop attaches no listener at this node (it delegates
  // from the root), so it'd fire too late in the bubble order to help — a
  // real addEventListener here runs first and stops it, leaving the strip's
  // own overflow-x:auto to handle the scroll.
  useEffect(() => {
    const dock = dockRef.current;
    if (!dock) {
      return;
    }
    const onWheel = (e: WheelEvent) => e.stopPropagation();
    dock.addEventListener("wheel", onWheel, { passive: true });
    return () => dock.removeEventListener("wheel", onWheel);
    // the dock only exists in the DOM while `open` — re-attach when it mounts
  }, [open]);

  // thumbnails/frames derive from scene elements; useUIAppState only fires on
  // appState changes — subscribe to scene changes, debounced a frame-ish
  const [thumbVersion, bumpThumbs] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = excalidrawAPI.onChange(() => {
      if (timer === null) {
        timer = setTimeout(() => {
          timer = null;
          bumpThumbs();
        }, 250);
      }
    });
    return () => {
      unsub();
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [excalidrawAPI]);

  const elements = excalidrawAPI?.getSceneElements() ?? [];
  // every surface shows ONE book at a time — the selected one, or the scene's
  // first when nothing is selected (or the selection has been deleted)
  const books = getFlipbookBooks(elements);
  const bookId = resolveBookId(elements, activeBookId);
  const frames = getFlipbookPages(elements, bookId ?? undefined);
  const activeIndex = Math.max(
    0,
    frames.findIndex((f) => f.id === activeId),
  );
  const activeFrame = frames[activeIndex] as
    | ExcalidrawFrameLikeElement
    | undefined;

  // page-management handlers live in one shared hook so the panel drives the
  // same flipbook without a second implementation
  const {
    goTo,
    addPage,
    duplicatePage,
    removeIds,
    applyFps,
    reorderPages,
    withGhostsSuspended,
  } = useFlipbookActions(excalidrawAPI);

  // ---- playback: the camera locks onto the current page (the "stage") and
  // every page's content is swapped in place via render-time overrides —
  // no camera cuts, so nothing on screen jumps or distorts. playMode decides
  // what happens at the ends (wrap, bounce, or stop). ----
  useEffect(() => {
    if (!playing || !excalidrawAPI) {
      return;
    }
    const startFrames = getFlipbookPages(
      excalidrawAPI.getSceneElements(),
      bookId ?? undefined,
    );
    if (startFrames.length === 0) {
      setPlaying(false);
      return;
    }
    const stage = startFrames[Math.min(activeIndex, startFrames.length - 1)];
    excalidrawAPI.setViewport({
      target: [stage],
      fit: "scale-down",
      animation: false,
    });
    const overrides = new Map<string, AnimatedValues>();
    let index = startFrames.findIndex((f) => f.id === stage.id);
    let dir = 1;
    let cancelled = false;

    const showPage = (pageIndex: number) => {
      const scene = excalidrawAPI.getSceneElements();
      const sceneFrames = getFlipbookPages(scene, bookId ?? undefined);
      const page = sceneFrames[pageIndex];
      if (!page) {
        return;
      }
      const map = arrayToMap(scene);
      overrides.clear();
      for (const f of sceneFrames) {
        const isCurrent = f.id === page.id;
        const dx = stage.x - f.x;
        const dy = stage.y - f.y;
        for (const el of getElementsOverlappingFrame(scene, f, map)) {
          // getElementsOverlappingFrame returns the frame itself too — the
          // member override would hide the stage's border/artboard whenever
          // another page is showing; frames are handled below instead
          if (el.id === f.id) {
            continue;
          }
          overrides.set(
            el.id,
            isCurrent ? { dx, dy, opacity: 1 } : { opacity: 0 },
          );
        }
        // hide the other pages' artboards/borders around the stage; the
        // current page's frame keeps dx/dy so its clip region (and members)
        // follow it onto the stage instead of clipping at the faraway slot
        if (f.id !== stage.id) {
          overrides.set(
            f.id,
            isCurrent ? { dx, dy, opacity: 0 } : { opacity: 0 },
          );
        }
      }
      setAnimationOverrides(overrides);
      setActiveId(page.id);
      excalidrawAPI.triggerRender(true);
      // the mini preview window renders on its own schedule — nudge it to
      // redraw the stage with the freshly swapped page
      window.dispatchEvent(
        new CustomEvent(FLIPBOOK_TICK_EVENT, { detail: { stageId: stage.id } }),
      );
    };

    const step = () => {
      if (cancelled) {
        return;
      }
      const current = getFlipbookPages(
        excalidrawAPI.getSceneElements(),
        bookId ?? undefined,
      );
      if (current.length === 0) {
        setPlaying(false);
        return;
      }
      index = Math.min(Math.max(index, 0), current.length - 1);
      showPage(index);
      playTimerRef.current = setTimeout(() => {
        const last = current.length - 1;
        if (playMode === "once" && index === last) {
          setPlaying(false);
          return;
        }
        if (playMode === "pingpong") {
          if (index === last) {
            dir = -1;
          } else if (index === 0) {
            dir = 1;
          }
          index += dir;
        } else {
          index = (index + 1) % current.length;
        }
        step();
      }, getScreenDurationMs(current[index]));
    };
    step();
    return () => {
      cancelled = true;
      if (playTimerRef.current) {
        clearTimeout(playTimerRef.current);
      }
      clearAnimationOverrides(overrides);
      excalidrawAPI.triggerRender(true);
      // let the mini preview repaint its normal (override-free) content
      window.dispatchEvent(new CustomEvent(FLIPBOOK_TICK_EVENT));
    };
    // restarting on activeIndex change would fight the playback's own steps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, playMode, excalidrawAPI, bookId]);

  // opening the bar adopts the scene's persisted rate (first page's duration)
  useEffect(() => {
    if (open && frames.length > 0) {
      const ms = getScreenDurationMs(frames[0]);
      setFps(Math.min(30, Math.max(1, Math.round(1000 / ms))));
    }
    // sync from the scene only on open — not while the user edits the field
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- onion skin: ghost up to `before` pages (past, red tint) and `after`
  // pages (future, green tint) onto the active page. Ghosts paint into a
  // passive overlay canvas floated over the active page — scene elements are
  // never touched, so every page keeps its own content visible on the canvas
  // (render-time overrides could only MOVE an element, emptying its home
  // slot while onion was on). Opacity falls off per step. ----
  const neighborKey = frames
    .slice(
      Math.max(0, activeIndex - onion.before),
      activeIndex + onion.after + 1,
    )
    .map((f) => f.id)
    .join(",");
  const showOnion =
    (open || flipbookActive) && onion.enabled && !playing && !!activeFrame;

  // keep the overlay glued to the active page across pan/zoom without React
  // re-renders — scroll isn't in UIAppState, so style it imperatively
  const positionOverlay = useCallback(() => {
    const overlay = onionOverlayRef.current;
    if (!overlay || !excalidrawAPI || !activeFrame) {
      return;
    }
    const state = excalidrawAPI.getAppState();
    const zoom = state.zoom.value;
    overlay.style.left = `${
      state.offsetLeft + (activeFrame.x + state.scrollX) * zoom
    }px`;
    overlay.style.top = `${
      state.offsetTop + (activeFrame.y + state.scrollY) * zoom
    }px`;
    overlay.style.width = `${activeFrame.width * zoom}px`;
    overlay.style.height = `${activeFrame.height * zoom}px`;
  }, [excalidrawAPI, activeFrame]);

  useEffect(() => {
    if (!showOnion || !excalidrawAPI) {
      return;
    }
    positionOverlay();
    return excalidrawAPI.onScrollChange(positionOverlay);
  }, [showOnion, excalidrawAPI, positionOverlay]);

  useEffect(() => {
    const overlay = onionOverlayRef.current;
    if (!showOnion || !overlay || !excalidrawAPI || !activeFrame) {
      return;
    }
    const scene = excalidrawAPI.getSceneElements();
    const map = arrayToMap(scene);
    const sceneFrames = getFlipbookPages(scene, bookId ?? undefined);
    const dpr = window.devicePixelRatio;
    // zoom-independent resolution (scene px × dpr): pan/zoom only restyles
    // the overlay via positionOverlay, no repaint needed
    overlay.width = Math.max(1, Math.round(activeFrame.width * dpr));
    overlay.height = Math.max(1, Math.round(activeFrame.height * dpr));
    const ctx = overlay.getContext("2d")!;
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    const scratch = document.createElement("canvas");
    scratch.width = overlay.width;
    scratch.height = overlay.height;
    const paintGhost = (
      neighbor: ExcalidrawFrameLikeElement | undefined,
      opacity: number,
      tint: string | undefined,
    ) => {
      if (!neighbor) {
        return;
      }
      const members = getElementsOverlappingFrame(scene, neighbor, map).filter(
        (el) => el.id !== neighbor.id,
      );
      if (members.length === 0) {
        return;
      }
      // playback overrides must never leak into ghosts (and there are no
      // onion overrides anymore) — draw the neighbor's true content
      withGhostsSuspended(() =>
        renderStaticScene(
          {
            canvas: scratch,
            rc: rough.canvas(scratch),
            elementsMap: toBrandedType<RenderableElementsMap>(
              arrayToMap(members),
            ),
            allElementsMap: toBrandedType<NonDeletedSceneElementsMap>(
              arrayToMap(scene),
            ),
            visibleElements: members,
            scale: dpr,
            appState: {
              ...appState,
              theme: THEME.LIGHT,
              // transparent: the ghost strokes composite over the real page
              viewBackgroundColor: "transparent",
              scrollX: -neighbor.x,
              scrollY: -neighbor.y,
              zoom: { value: 1 },
              shouldCacheIgnoreZoom: false,
              selectedElementIds: {},
              // no frame in `members` → no artboard fill; skip clipping so
              // strokes overshooting the page edge still ghost naturally
              frameRendering: {
                enabled: false,
                outline: false,
                name: false,
                clip: false,
              },
            } as AppState,
            renderConfig: {
              canvasBackgroundColor: "transparent",
              imageCache: sharedImageCache,
              renderGrid: false,
              isExporting: true,
              embedsValidationStatus: new Map(),
              elementsPendingErasure: new Set(),
              pendingFlowchartNodes: null,
              theme: THEME.LIGHT,
            },
          },
          // un-throttled: sharing the throttled slot would clobber the main
          // canvas's pending render args
          false,
        ),
      );
      const sctx = scratch.getContext("2d")!;
      if (tint) {
        sctx.save();
        sctx.globalCompositeOperation = "source-atop";
        sctx.globalAlpha = 0.65;
        sctx.fillStyle = tint;
        sctx.fillRect(0, 0, scratch.width, scratch.height);
        sctx.restore();
      }
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.drawImage(scratch, 0, 0);
      ctx.restore();
    };
    for (let k = 1; k <= onion.before; k++) {
      paintGhost(
        sceneFrames[activeIndex - k],
        onion.opacity * 0.6 ** (k - 1),
        onion.tint ? TINT_PREV : undefined,
      );
    }
    for (let k = 1; k <= onion.after; k++) {
      paintGhost(
        sceneFrames[activeIndex + k],
        onion.opacity * 0.5 * 0.6 ** (k - 1),
        onion.tint ? TINT_NEXT : undefined,
      );
    }
    positionOverlay();
    // ghost images/textures decode async — repaint once they're cached
    ensureImageCache(scene, excalidrawAPI.getFiles(), bumpThumbs);
    // activeFrame identity churns per render; the ids/positions below capture
    // what the ghosts actually depend on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excalidrawAPI,
    showOnion,
    onion,
    activeIndex,
    activeFrame?.id,
    activeFrame?.x,
    activeFrame?.y,
    activeFrame?.width,
    activeFrame?.height,
    neighborKey,
    thumbVersion,
    positionOverlay,
    bookId,
  ]);

  const openFlipbook = useCallback(() => {
    setOpen(true);
    const scene = excalidrawAPI?.getSceneElements() ?? [];
    const current = getFlipbookPages(
      scene,
      resolveBookId(scene, activeBookId) ?? undefined,
    );
    if (current.length === 0) {
      addPage();
    } else {
      goTo(current[0], true);
    }
    // pages are drawn frame-by-frame — start with the pen in hand
    excalidrawAPI?.setActiveTool({ type: "freedraw" });
    // addPage/goTo close over current scene state; run-once semantics are fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI]);

  // other UI (the animation bar's flipbook button) can open the strip
  useEffect(() => {
    const onOpen = () => openFlipbook();
    window.addEventListener(FLIPBOOK_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(FLIPBOOK_OPEN_EVENT, onOpen);
  }, [openFlipbook]);

  // the page menu closes itself (FloatingMenu owns outside-click + Escape);
  // this covers the onion popover, which is anchored, not floating
  const closeMenu = useCallback(() => setMenu(null), []);
  useEffect(() => {
    if (!onionMenuOpen) {
      return;
    }
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".flipbook-pop") &&
        !target.closest(".flipbook-bar__toggle")
      ) {
        setOnionMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [onionMenuOpen]);

  if (!excalidrawAPI) {
    return null;
  }

  // the ghost overlay floats over the active page; positionOverlay styles it
  const onionOverlay = showOnion ? (
    <canvas ref={onionOverlayRef} className="flipbook-onion-overlay" />
  ) : null;

  // no standalone trigger in design mode — the flipbook opens from the
  // Animation tab (FlipbookPanel / the animation bar's filmstrip button);
  // the onion overlay still renders while the panel drives the flipbook
  if (!open) {
    return onionOverlay;
  }

  const menuFrame = menu ? frames.find((f) => f.id === menu.frameId) : null;

  return (
    <>
      {onionOverlay}
      <div className="flipbook-dock" ref={dockRef}>
        <div className="flipbook-bar">
          <div className="flipbook-bar__header">
            <button
              className={`flipbook-bar__play${
                playing ? " flipbook-bar__play--playing" : ""
              }`}
              title={playing ? "Pause" : "Play"}
              // the e2e reads this rather than the label text, which is i18n-
              // and design-volatile
              data-playing={playing ? "true" : "false"}
              disabled={frames.length < 2}
              onClick={() => setPlaying(!playing)}
            >
              {playing ? pauseIcon : playIcon}
              {playing ? "Pause" : "Play"}
            </button>

            <label className="flipbook-bar__fps" title="Frames per second">
              <input
                type="number"
                min={1}
                max={30}
                value={fps}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (e.target.value !== "" && Number.isFinite(value)) {
                    setFps(value);
                  }
                }}
                onBlur={(e) => applyFps(Number(e.target.value))}
              />
              fps
            </label>

            <div className="flipbook-bar__spacer" />

            <button
              className="flipbook-bar__mode"
              title={`Playback: ${
                PLAY_MODES.find((m) => m.id === playMode)!.label
              } — click to change`}
              onClick={() => {
                const i = PLAY_MODES.findIndex((m) => m.id === playMode);
                setPlayMode(PLAY_MODES[(i + 1) % PLAY_MODES.length].id);
              }}
            >
              {PLAY_MODES.find((m) => m.id === playMode)!.icon}
            </button>

            <button
              className={`flipbook-bar__toggle${
                onion.enabled ? " flipbook-bar__toggle--on" : ""
              }`}
              title="Onion skin settings"
              onClick={() => setOnionMenuOpen(!onionMenuOpen)}
            >
              {onionIcon}
            </button>

            <button
              className="flipbook-bar__add"
              title="Add frame after current"
              onClick={() => addPage(activeFrame)}
            >
              {addIcon}
            </button>

            <div className="flipbook-bar__sep" />

            <button
              className="flipbook-bar__close"
              title="Close flipbook"
              onClick={() => {
                setPlaying(false);
                setOpen(false);
              }}
            >
              {closeIcon}
            </button>
          </div>

          <div className="flipbook-bar__track">
            <label
              className="flipbook-bar__label"
              title="Onion skin for this flipbook"
            >
              <input
                type="checkbox"
                checked={onion.enabled}
                onChange={(e) =>
                  setOnion({ ...onion, enabled: e.target.checked })
                }
              />
              <span className="flipbook-bar__label-text">
                {books.length > 1 ? (
                  <select
                    className="flipbook-bar__book"
                    title="Which flipbook this strip is editing"
                    value={bookId ?? ""}
                    onChange={(e) => {
                      setActiveBookId(e.target.value);
                      setActiveId(null);
                    }}
                  >
                    {books.map((book, i) => (
                      <option key={book.id} value={book.id}>
                        {`Track ${i + 1}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="flipbook-bar__label-name">Track 1</span>
                )}
                <span className="flipbook-bar__label-count">
                  {frames.length} {frames.length === 1 ? "frame" : "frames"}
                </span>
              </span>
            </label>

            <div
              className="flipbook-bar__strip"
              // cap at VISIBLE_THUMBS pages, then scroll — pages share a size in
              // practice, so the first one's width sizes the whole window
              style={{
                maxWidth: `min(${
                  VISIBLE_THUMBS *
                    (thumbWidth(frames[0] ?? { width: 4, height: 3 }) +
                      STRIP_GAP) +
                  STRIP_PADDING * 2
                }px, calc(100vw - 22rem))`,
              }}
            >
              {frames.map((frame, index) => (
                <FrameThumb
                  key={frame.id}
                  frame={frame}
                  index={index}
                  active={index === activeIndex}
                  dropTarget={dropIndex === index && dragId !== frame.id}
                  appState={appState}
                  excalidrawAPI={excalidrawAPI}
                  thumbVersion={thumbVersion}
                  withGhostsSuspended={withGhostsSuspended}
                  onClick={() => goTo(frame, true)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenu({ frameId: frame.id, x: e.clientX, y: e.clientY });
                  }}
                  onDragStart={() => setDragId(frame.id)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropIndex(index);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId) {
                      reorderPages(dragId, index);
                    }
                    setDragId(null);
                    setDropIndex(null);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setDropIndex(null);
                  }}
                />
              ))}
            </div>
          </div>

          {onionMenuOpen && (
            <div className="flipbook-pop">
              <label className="flipbook-pop__row">
                <input
                  type="checkbox"
                  checked={onion.enabled}
                  onChange={(e) =>
                    setOnion({ ...onion, enabled: e.target.checked })
                  }
                />
                Onion skin
              </label>
              <label className="flipbook-pop__row">
                Opacity
                <input
                  type="range"
                  min={0.05}
                  max={0.8}
                  step={0.05}
                  value={onion.opacity}
                  disabled={!onion.enabled}
                  onChange={(e) =>
                    setOnion({ ...onion, opacity: Number(e.target.value) })
                  }
                />
              </label>
              <label className="flipbook-pop__row">
                Frames before
                <input
                  type="number"
                  min={0}
                  max={3}
                  value={onion.before}
                  disabled={!onion.enabled}
                  onChange={(e) =>
                    setOnion({
                      ...onion,
                      before: Math.min(
                        3,
                        Math.max(0, Number(e.target.value) || 0),
                      ),
                    })
                  }
                />
              </label>
              <label className="flipbook-pop__row">
                Frames after
                <input
                  type="number"
                  min={0}
                  max={3}
                  value={onion.after}
                  disabled={!onion.enabled}
                  onChange={(e) =>
                    setOnion({
                      ...onion,
                      after: Math.min(
                        3,
                        Math.max(0, Number(e.target.value) || 0),
                      ),
                    })
                  }
                />
              </label>
              <label
                className="flipbook-pop__row"
                title="Past frames red, future frames green"
              >
                <input
                  type="checkbox"
                  checked={onion.tint}
                  disabled={!onion.enabled}
                  onChange={(e) =>
                    setOnion({ ...onion, tint: e.target.checked })
                  }
                />
                Color tint
              </label>
            </div>
          )}
        </div>
      </div>
      {menu && menuFrame && (
        <FloatingMenu x={menu.x} y={menu.y} onClose={closeMenu}>
          <button onClick={() => duplicatePage(menuFrame)}>
            Duplicate frame
          </button>
          <button
            onClick={() => removeIds(clearPageIds(elements, menuFrame.id))}
          >
            Clear frame
          </button>
          <button
            className="flipbook-menu__danger"
            onClick={() => {
              removeIds(deletePageIds(elements, menuFrame.id));
              if (activeId === menuFrame.id) {
                setActiveId(null);
              }
            }}
          >
            Delete frame
          </button>
        </FloatingMenu>
      )}
    </>
  );
};

/** One filmstrip thumbnail — the page rendered tiny via the real static-scene
 * renderer (clipped to the frame, like export). Onion-skin overrides are
 * suspended around the synchronous render so ghosts never leak into thumbs. */
export const FrameThumb = ({
  frame,
  index,
  active,
  dropTarget,
  appState,
  excalidrawAPI,
  thumbVersion,
  withGhostsSuspended,
  onClick,
  onContextMenu,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  frame: ExcalidrawFrameLikeElement;
  index: number;
  active: boolean;
  dropTarget: boolean;
  appState: ReturnType<typeof useUIAppState>;
  excalidrawAPI: NonNullable<ReturnType<typeof useExcalidrawAPI>>;
  thumbVersion: number;
  withGhostsSuspended: (draw: () => void) => void;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const width = thumbWidth(frame);
  // Every thumbnail draw is a full renderStaticScene over the whole scene, so
  // a 20-page book cost 20 of them on mount and 20 more on every scene change
  // — most of them for pages scrolled out of the strip. Draw only what's on
  // screen; a thumb that scrolls in redraws then (this effect re-runs), which
  // is also how it catches up on edits made while it was hidden.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const button = buttonRef.current;
    return button ? observeVisibility(button, setVisible) : undefined;
  }, []);

  // only a few pages fit the strip — keep the current page in view as playback
  // advances or the panel jumps to a page further along
  useEffect(() => {
    if (active) {
      buttonRef.current?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !visible) {
      return;
    }
    const elements = excalidrawAPI.getSceneElements();
    const zoomValue = Math.min(
      width / frame.width,
      THUMB_HEIGHT / frame.height,
    );
    // renderStaticScene reads the global animation overrides — detach the
    // onion ghosts for this synchronous draw so thumbs show true content
    const draw = () =>
      withGhostsSuspended(() =>
        renderStaticScene(
          {
            canvas,
            rc: rough.canvas(canvas),
            elementsMap: toBrandedType<RenderableElementsMap>(
              arrayToMap(elements),
            ),
            allElementsMap: toBrandedType<NonDeletedSceneElementsMap>(
              arrayToMap(elements),
            ),
            visibleElements: elements,
            scale: window.devicePixelRatio,
            appState: {
              ...appState,
              // the artwork's own colors, never the UI theme (staticScene.ts)
              theme: THEME.LIGHT,
              scrollX: -frame.x,
              scrollY: -frame.y,
              zoom: { value: zoomValue },
              shouldCacheIgnoreZoom: false,
              selectedElementIds: {},
              frameRendering: {
                enabled: true,
                outline: false,
                name: false,
                clip: true,
              },
            } as AppState,
            renderConfig: {
              canvasBackgroundColor: appState.viewBackgroundColor,
              imageCache: sharedImageCache,
              renderGrid: false,
              isExporting: true,
              embedsValidationStatus: new Map(),
              elementsPendingErasure: new Set(),
              pendingFlowchartNodes: null,
              theme: THEME.LIGHT,
            },
          },
          // un-throttled: sharing the throttled slot would clobber the main
          // canvas's pending render args
          false,
        ),
      );
    // one frame late, on purpose: a whole-scene render per thumb would
    // otherwise land in the same frame as the panel/strip appearing, which is
    // what made opening Flipbook mode feel like a stall
    const raf = requestAnimationFrame(() => {
      draw();
      // image/texture files decode async — repaint once they land in the cache
      ensureImageCache(elements, excalidrawAPI.getFiles(), draw);
    });
    return () => cancelAnimationFrame(raf);
    // the render body spreads appState, but only bg/theme should trigger a
    // redraw — scroll/zoom churn would repaint every thumb per frame
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excalidrawAPI,
    frame,
    width,
    visible,
    thumbVersion,
    withGhostsSuspended,
    appState.viewBackgroundColor,
    appState.theme,
  ]);

  return (
    <button
      ref={buttonRef}
      className={`flipbook-thumb${active ? " flipbook-thumb--active" : ""}${
        dropTarget ? " flipbook-thumb--drop" : ""
      }`}
      title={`${
        frame.name || `Frame ${index + 1}`
      } — drag to reorder, right-click for options`}
      onClick={onClick}
      onContextMenu={onContextMenu}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <span className="flipbook-thumb__num">{index + 1}</span>
      <span className="flipbook-thumb__frame">
        <canvas
          ref={canvasRef}
          width={width * window.devicePixelRatio}
          height={THUMB_HEIGHT * window.devicePixelRatio}
          style={{ width, height: THUMB_HEIGHT }}
        />
      </span>
    </button>
  );
};
